import { useAuthContext } from '@/auth/useAuthContext';
import { createVehicle, deleteVehicle, listVehicles, updateVehicle } from '@/lib/api';
import type { CreateVehicleInput, Facility, UpdateVehicleInput, Vehicle } from '@/lib/types';
import { Car, Edit2, Plus, ToggleLeft, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Vehicle form (add + edit)
// ---------------------------------------------------------------------------

interface VehicleFormState {
  name: string;
  registration: string;
  facilityId: string;
  capacityPets: string;
  hasAircon: boolean;
  hasCrates: boolean;
  notes: string;
}

const defaultForm = (): VehicleFormState => ({
  name: '',
  registration: '',
  facilityId: '',
  capacityPets: '4',
  hasAircon: false,
  hasCrates: false,
  notes: '',
});

const formFromVehicle = (v: Vehicle): VehicleFormState => ({
  name: v.name,
  registration: v.registration ?? '',
  facilityId: v.facilityId ?? '',
  capacityPets: String(v.capacityPets),
  hasAircon: Boolean(v.hasAircon),
  hasCrates: Boolean(v.hasCrates),
  notes: v.notes ?? '',
});

// ---------------------------------------------------------------------------
// VehiclesSettings
// ---------------------------------------------------------------------------

export function VehiclesSettings() {
  const { state } = useAuthContext();
  const facilities: Facility[] = state.me?.facilitiesInScope ?? [];

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add form state
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState<VehicleFormState>(defaultForm());
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Edit state
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<VehicleFormState>(defaultForm());
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    listVehicles()
      .then((r) => setVehicles(r.vehicles))
      .catch(() => setError('Failed to load vehicles'))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    setAddBusy(true);
    setAddError(null);
    try {
      const input: CreateVehicleInput = {
        name: addForm.name.trim(),
        registration: addForm.registration.trim() || null,
        facilityId: addForm.facilityId || null,
        capacityPets: Math.max(1, Number.parseInt(addForm.capacityPets, 10) || 4),
        hasAircon: addForm.hasAircon,
        hasCrates: addForm.hasCrates,
        notes: addForm.notes.trim() || null,
      };
      const { vehicle } = await createVehicle(input);
      setVehicles((v) => [...v, vehicle]);
      setAdding(false);
      setAddForm(defaultForm());
    } catch {
      setAddError('Failed to create vehicle');
    } finally {
      setAddBusy(false);
    }
  };

  const handleEdit = async () => {
    if (!editId) return;
    setEditBusy(true);
    setEditError(null);
    try {
      const input: UpdateVehicleInput = {
        name: editForm.name.trim(),
        registration: editForm.registration.trim() || null,
        facilityId: editForm.facilityId || null,
        capacityPets: Math.max(1, Number.parseInt(editForm.capacityPets, 10) || 4),
        hasAircon: editForm.hasAircon,
        hasCrates: editForm.hasCrates,
        notes: editForm.notes.trim() || null,
      };
      const { vehicle } = await updateVehicle(editId, input);
      setVehicles((v) => v.map((x) => (x.id === vehicle.id ? vehicle : x)));
      setEditId(null);
    } catch {
      setEditError('Failed to update vehicle');
    } finally {
      setEditBusy(false);
    }
  };

  const handleDeactivate = async (id: string, current: number) => {
    try {
      const { vehicle } = await updateVehicle(id, { isActive: !current });
      setVehicles((v) => v.map((x) => (x.id === vehicle.id ? vehicle : x)));
    } catch {
      /* noop */
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}" permanently? This cannot be undone.`)) return;
    try {
      await deleteVehicle(id);
      setVehicles((v) => v.filter((x) => x.id !== id));
    } catch {
      setError('Failed to delete vehicle');
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <header className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground/70">
            Operations · Vehicles
          </p>
          <h2 className="mt-1 text-2xl font-medium tracking-tight">Vehicles</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage pickup and dropoff vehicles for your kennels.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setAdding(true);
            setAddForm(defaultForm());
          }}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          Add vehicle
        </button>
      </header>

      {adding && (
        <VehicleForm
          title="Add vehicle"
          form={addForm}
          facilities={facilities}
          busy={addBusy}
          error={addError}
          onChangeForm={setAddForm}
          onSubmit={handleAdd}
          onCancel={() => setAdding(false)}
        />
      )}

      {vehicles.length === 0 && !adding && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Car className="size-10 text-muted-foreground/50" strokeWidth={1.25} />
          <p className="text-sm text-muted-foreground">No vehicles added yet.</p>
        </div>
      )}

      <ul className="space-y-2">
        {vehicles.map((v) => (
          <li key={v.id} className="rounded-xl border border-border bg-surface p-4 ring-0 elevate">
            {editId === v.id ? (
              <VehicleForm
                title="Edit vehicle"
                form={editForm}
                facilities={facilities}
                busy={editBusy}
                error={editError}
                onChangeForm={setEditForm}
                onSubmit={handleEdit}
                onCancel={() => setEditId(null)}
              />
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div
                    className={[
                      'mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg',
                      v.isActive ? 'bg-primary/10' : 'bg-muted',
                    ].join(' ')}
                  >
                    <Car
                      className={[
                        'size-4',
                        v.isActive ? 'text-primary' : 'text-muted-foreground',
                      ].join(' ')}
                      strokeWidth={1.5}
                    />
                  </div>
                  <div>
                    <p
                      className={[
                        'text-sm font-medium',
                        !v.isActive && 'text-muted-foreground',
                      ].join(' ')}
                    >
                      {v.name}
                      {!v.isActive && (
                        <span className="ml-2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                          Inactive
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {v.registration && `${v.registration} · `}
                      {v.capacityPets} pet{v.capacityPets !== 1 ? 's' : ''}
                      {v.hasAircon ? ' · Air con' : ''}
                      {v.hasCrates ? ' · Crates' : ''}
                    </p>
                    {v.notes && <p className="mt-1 text-xs text-muted-foreground">{v.notes}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {v.isActive ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setEditId(v.id);
                          setEditForm(formFromVehicle(v));
                          setEditError(null);
                        }}
                        className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary press"
                        title="Edit"
                      >
                        <Edit2 className="size-3.5" strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeactivate(v.id, v.isActive)}
                        className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary press"
                        title="Deactivate"
                      >
                        <ToggleLeft className="size-3.5" strokeWidth={1.75} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleDeactivate(v.id, v.isActive)}
                        className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary press"
                        title="Reactivate"
                      >
                        <ToggleLeft className="size-3.5" strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(v.id, v.name)}
                        className="grid size-8 place-items-center rounded-md text-destructive hover:bg-destructive/10 press"
                        title="Delete permanently"
                      >
                        <Trash2 className="size-3.5" strokeWidth={1.75} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared vehicle form sub-component
// ---------------------------------------------------------------------------

interface VehicleFormProps {
  title: string;
  form: VehicleFormState;
  facilities: Facility[];
  busy: boolean;
  error: string | null;
  onChangeForm: (form: VehicleFormState) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

function VehicleForm({
  title,
  form,
  facilities,
  busy,
  error,
  onChangeForm,
  onSubmit,
  onCancel,
}: VehicleFormProps) {
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-4">
      <p className="text-sm font-medium">{title}</p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Vehicle name *">
          <input
            type="text"
            value={form.name}
            onChange={(e) => onChangeForm({ ...form, name: e.target.value })}
            placeholder="Van 1"
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
        </Field>
        <Field label="Registration plate">
          <input
            type="text"
            value={form.registration}
            onChange={(e) => onChangeForm({ ...form, registration: e.target.value })}
            placeholder="AB12 CDE"
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm uppercase outline-none focus:ring-2 focus:ring-ring/30"
          />
        </Field>
        <Field label="Max pets capacity">
          <input
            type="number"
            min={1}
            max={50}
            value={form.capacityPets}
            onChange={(e) => onChangeForm({ ...form, capacityPets: e.target.value })}
            className="num h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
        </Field>
        {facilities.length > 1 && (
          <Field label="Facility (optional)">
            <select
              value={form.facilityId}
              onChange={(e) => onChangeForm({ ...form, facilityId: e.target.value })}
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
            >
              <option value="">All facilities</option>
              {facilities.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.hasAircon}
            onChange={(e) => onChangeForm({ ...form, hasAircon: e.target.checked })}
            className="size-4 rounded border-border"
          />
          Air conditioning
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.hasCrates}
            onChange={(e) => onChangeForm({ ...form, hasCrates: e.target.checked })}
            className="size-4 rounded border-border"
          />
          Crates fitted
        </label>
      </div>
      <Field label="Notes (optional)">
        <textarea
          value={form.notes}
          onChange={(e) => onChangeForm({ ...form, notes: e.target.value })}
          rows={2}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          placeholder="Any additional information…"
        />
      </Field>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy || !form.name.trim()}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground press"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}
